import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

const BASE_URL = "/api";

type DocItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

export default function StaffDocumentDetail() {
  const navigate = useNavigate();
  const { merchantId, documentKey } = useParams<{
    merchantId: string;
    documentKey: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState<DocItem | null>(null);
  const [error, setError] = useState("");

  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [uiMsg, setUiMsg] = useState("");

  const viewUrl = useMemo(() => {
    if (!merchantId || !documentKey) return "";
    return `${BASE_URL}/staff/documents/${merchantId}/${documentKey}/view`;
  }, [merchantId, documentKey]);

  const downloadUrl = useMemo(() => {
    if (!merchantId || !documentKey) return "";
    return `${BASE_URL}/staff/documents/${merchantId}/${documentKey}/download`;
  }, [merchantId, documentKey]);

  // ✅ Get doc info by calling list endpoint and picking the doc
  useEffect(() => {
    const run = async () => {
      if (!merchantId || !documentKey) {
        setError("URLが正しくありません。");
        setLoading(false);
        return;
      }

      setError("");
      setUiMsg("");
      setLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/documents/${merchantId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "documents fetch failed");

        const items: DocItem[] = Array.isArray(json?.items) ? json.items : [];
        const found = items.find((x) => x.document_key === documentKey) || null;

        if (!found) {
          setError("書類が見つかりません。");
          setLoading(false);
          return;
        }

        setDoc(found);
        setLoading(false);
      } catch (e: any) {
        setError(e?.message || "書類情報の取得に失敗しました。");
        setLoading(false);
      }
    };

    run();
  }, [merchantId, documentKey]);

  const onDelete = async () => {
    if (!merchantId || !documentKey) return;

    const ok = window.confirm("本当に削除しますか？この操作は取り消せません。");
    if (!ok) return;

    setUiMsg("");
    setDeleting(true);

    try {
      const res = await fetch(`${BASE_URL}/staff/documents/${merchantId}/${documentKey}`, {
        method: "DELETE",
      });

      const json = await safeJson(res);

      if (!res.ok) {
        setUiMsg(json?.detail || "削除に失敗しました。");
        setDeleting(false);
        return;
      }

      navigate(`/staff/progress/${merchantId}`);
    } catch {
      setUiMsg("削除に失敗しました。バックエンドが起動しているか確認してください。");
      setDeleting(false);
    }
  };

  const onReplace = async (file: File) => {
    if (!merchantId || !documentKey) return;

    setUiMsg("");
    setReplacing(true);

    try {
      const fd = new FormData();
      fd.append("upload_file", file);

      const res = await fetch(`${BASE_URL}/staff/documents/${merchantId}/${documentKey}/replace`, {
        method: "POST",
        body: fd,
      });

      const json = await safeJson(res);

      if (!res.ok) {
        setUiMsg(json?.detail || "差し替えに失敗しました。");
        setReplacing(false);
        return;
      }

      setUiMsg("差し替えしました。");
      // refresh doc info quickly (re-fetch list and pick again)
      setTimeout(() => window.location.reload(), 400);
    } catch {
      setUiMsg("差し替えに失敗しました。バックエンドが起動しているか確認してください。");
      setReplacing(false);
    }
  };

  const title = doc
    ? doc.other_title
      ? `${doc.document_label}（${doc.other_title}）`
      : doc.document_label
    : "書類詳細";

  return (
    <StaffLayout title="書類詳細" active="progress">
      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && <div style={errBox}>{error}</div>}

      {!loading && !error && doc && (
        <>
          {uiMsg && <div style={msgBox}>{uiMsg}</div>}

          {/* Top info card */}
          <div style={card}>
            {/* ✅ Top button row (same order as form screen) */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <button
                onClick={() => (merchantId ? navigate(`/staff/progress/${merchantId}`) : navigate("/staff/progress"))}
                style={{ ...btnSecondary, marginBottom: 0 }}
              >
                進捗詳細へ戻る
              </button>

              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                style={{ ...btnPrimary, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                ダウンロード
              </a>

              <label
                style={{
                  ...btnSecondary,
                  marginBottom: 0,
                  cursor: replacing ? "not-allowed" : "pointer",
                  opacity: replacing ? 0.7 : 1,
                }}
              >
                {replacing ? "差し替え中..." : "差し替え"}
                <input
                  type="file"
                  style={{ display: "none" }}
                  disabled={replacing}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    onReplace(f);
                    e.currentTarget.value = "";
                  }}
                />
              </label>

              <button
                onClick={onDelete}
                disabled={deleting}
                style={{ ...btnDanger, opacity: deleting ? 0.7 : 1, cursor: deleting ? "not-allowed" : "pointer" }}
              >
                {deleting ? "削除中..." : "削除"}
              </button>
            </div>

            <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 10 }}>{title}</div>

            <div style={{ color: "#0f172a", lineHeight: 1.8, fontWeight: 700, fontSize: 13 }}>
              <div>加盟店ID：{merchantId}</div>
              <div>書類キー：{doc.document_key}</div>
              <div>ファイル名：{doc.file_name}</div>
              <div style={{ color: "#475569", fontWeight: 700 }}>提出日：{doc.uploaded_at}</div>
            </div>
          </div>

          {/* Preview */}
          <div style={{ marginTop: 12, ...card }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>プレビュー</div>

            <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
              {/* ✅ Works for PDFs + images (browser handles) */}
              <iframe
                title="preview"
                src={viewUrl}
                style={{ width: "100%", height: "70vh", border: "none", background: "#fff" }}
              />
            </div>

            <div style={{ marginTop: 10, color: "#64748b", fontWeight: 700, fontSize: 12, lineHeight: 1.6 }}>
              ※ PDFの場合はブラウザ内で表示されます。表示できない場合は「ダウンロード」をご利用ください。
            </div>
          </div>
        </>
      )}
    </StaffLayout>
  );
}

async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/** Styles */
const card: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 14,
  background: "#fff",
};

const btnPrimary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  background: "#0b1f3b",
  color: "#fff",
  fontWeight: 900,
};

const btnSecondary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900,
  cursor: "pointer",
  marginBottom: 12,
};

const btnDanger: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #fecaca",
  background: "#fff",
  color: "#b91c1c",
  fontWeight: 900,
};

const errBox: React.CSSProperties = {
  marginTop: 10,
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  color: "#b91c1c",
  padding: "10px 12px",
  borderRadius: 12,
  fontWeight: 900,
};

const msgBox: React.CSSProperties = {
  marginBottom: 10,
  background: "#f0f9ff",
  border: "1px solid #bae6fd",
  color: "#0b1f3b",
  padding: "10px 12px",
  borderRadius: 12,
  fontWeight: 900,
};
